import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ReportNotificationEmailsField from '../reportPreview/ReportNotificationEmailsField'
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

import type { WpsEditableSnapshot } from '../WpsPreviewPage'
import type { WpsPreviewDiff } from './wpsPreviewDiff'

/**
 * Confirmation dialog shown before persisting WPS Preview edits. WPS-
 * side parallel of `PqrDiffConfirmDialog`. Driven from
 * `WpsPreviewPage` by a `confirmMode` state — both Save and
 * Save & Generate route through this dialog first so the user can
 * eyeball exactly what is about to land on the server.
 *
 *   - `mode === 'save'`             : commit to DB only (no PDF generated)
 *   - `mode === 'saveAndGenerate'`  : commit AND generate the PDF report
 *
 * On the very first save for a pipe set the diff list is empty (no
 * baseline yet); the dialog falls back to a "first WPS draft" summary
 * showing the identifier values that will be persisted.
 *
 * Added in `wps-product-parity-fixes` slice C.3.
 */
export type WpsConfirmMode = 'save' | 'saveAndGenerate'

interface Props {
  open: boolean
  busy: boolean
  diff: WpsPreviewDiff
  firstSave: boolean
  snapshot: WpsEditableSnapshot
  mode: WpsConfirmMode
  /** When true with saveAndGenerate, hide the change summary and show emails only. */
  emailOnly?: boolean
  onCancel: () => void
  onConfirm: (args: { notificationEmails: string[] }) => void
}

function titleFor(mode: WpsConfirmMode, firstSave: boolean): string {
  if (mode === 'save') {
    return firstSave ? 'Save first WPS draft?' : 'Save changes?'
  }
  return firstSave ? 'Save first WPS draft & generate PDF?' : 'Save changes & generate PDF?'
}

function descriptionFor(mode: WpsConfirmMode): string {
  if (mode === 'save') {
    return 'These changes will be persisted to the database. No PDF will be generated.'
  }
  return 'These changes will be persisted to the database and a new WPS PDF will be generated.'
}

function confirmLabelFor(mode: WpsConfirmMode, busy: boolean): string {
  if (mode === 'save') {
    return busy ? 'Saving…' : 'Save'
  }
  return busy ? 'Saving…' : 'Save & Generate'
}

export default function WpsDiffConfirmDialog({
  open,
  busy,
  diff,
  firstSave,
  snapshot,
  mode,
  emailOnly = false,
  onCancel,
  onConfirm
}: Props) {
  const [notificationEmails, setNotificationEmails] = useState<string[]>([])

  useEffect(() => {
    if (!open) {
      setNotificationEmails([])
    }
  }, [open])

  if (!open) return null

  const Icon = mode === 'saveAndGenerate' ? DocumentArrowDownIcon : CheckCircleIcon

  const content = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wps-confirm-title"
      data-testid="wps-diff-confirm-dialog"
    >
      <div className="relative w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 id="wps-confirm-title" className="text-lg font-semibold text-gray-900">
            {titleFor(mode, firstSave)}
          </h2>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-2 text-sm text-gray-700">{descriptionFor(mode)}</p>

        {emailOnly && mode === 'saveAndGenerate' ? null : (
        <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-md border border-gray-200">
          {firstSave ? (
            <div className="p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                First WPS draft for this pipe set
              </p>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th
                      scope="col"
                      className="w-1/3 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                    >
                      Field
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                    >
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <SummaryTableRow label="WPS No." value={snapshot.identifiers.wpsNumber} />
                  <SummaryTableRow label="Rev." value={snapshot.identifiers.revision} />
                  <SummaryTableRow label="Application" value={snapshot.identifiers.application} />
                  <SummaryTableRow
                    label="Equipment"
                    value={snapshot.identifiers.equipmentDescription}
                  />
                </tbody>
              </table>
            </div>
          ) : diff.hasChanges ? (
            <div className="divide-y divide-gray-100" data-testid="wps-diff-confirm-groups">
              {diff.groups.map((group) => (
                <div
                  key={group.group}
                  className="p-4"
                  data-testid={`wps-diff-confirm-group-${group.group}`}
                >
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.group}
                  </p>
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th
                          scope="col"
                          className="w-1/3 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                        >
                          Field
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                        >
                          Previous value
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                        >
                          Updated value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((row, idx) => (
                        <tr
                          key={row.field}
                          className={
                            idx % 2 === 0
                              ? 'bg-white align-top'
                              : 'bg-gray-50/60 align-top'
                          }
                        >
                          <td className="px-3 py-2 text-xs font-semibold text-gray-800">
                            {row.field}:
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 line-through">
                            {row.before}
                          </td>
                          <td className="px-3 py-2 text-xs font-medium text-gray-900">
                            {row.after}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            <p className="p-4 text-sm text-gray-500" data-testid="wps-diff-confirm-empty">
              No changes detected.
            </p>
          )}
        </div>
        )}

        {mode === 'saveAndGenerate' ? (
          <ReportNotificationEmailsField
            emails={notificationEmails}
            onChange={setNotificationEmails}
            disabled={busy}
          />
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={busy}
            data-testid="wps-diff-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onConfirm({ notificationEmails })}
            disabled={busy}
            data-testid="wps-diff-confirm-confirm"
          >
            <Icon className="h-4 w-4" />
            {confirmLabelFor(mode, busy)}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}

interface SummaryTableRowProps {
  label: string
  value: string
}

function SummaryTableRow({ label, value }: SummaryTableRowProps) {
  return (
    <tr className="border-b border-gray-100 last:border-b-0 align-top">
      <td className="px-3 py-2 text-xs font-semibold text-gray-800">{label}:</td>
      <td className="whitespace-pre-wrap px-3 py-2 text-sm text-gray-900">
        {value || <span className="text-gray-400">&mdash;</span>}
      </td>
    </tr>
  )
}
